import {TKey, useTranslation} from '@/translations/i18n'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {z, ZodSchema} from 'zod'
import {ObjectKeys} from '@/utilities/helpers'
import {FormSection} from '@/components/commons/form-section/FormSection'
import {MedicalReportSectionCopy} from '@/features/medical-reports/types'
import {useGetPreviousReportByProject} from '@/features/medical-reports/queries/useGetPreviousReportByProject'
import {DIAGNOSIS_TEXTAREA_LIMIT, REPORT_TEXTAREA_LIMIT} from '@/features/medical-reports/formSchemas'
import {MedicalReportTextArea} from '@/features/medical-reports/components/medical-report-text-area/MedicalReportTextArea'

// Per-field overrides of the shared textarea limit
const sectionsMaxLength: Record<string, number> = {
    diagnosis: DIAGNOSIS_TEXTAREA_LIMIT
}

interface TextAreasSectionForm<T extends string = string> {
    sectionsMap: Record<T, MedicalReportSectionCopy>
    schema: ZodSchema
    hasDividers?: boolean
}

export const MedicalReportTextAreaSections = ({sectionsMap, schema, hasDividers}: TextAreasSectionForm) => {
    const {t} = useTranslation()
    const form = useFormContext<z.infer<typeof schema>>()
    const projectId = useWatch({control: form.control, name: 'project.id'})
    const medicalReportTypeId = useWatch({control: form.control, name: 'reportType.id'})

    const previousProjectReportQuery = useGetPreviousReportByProject({
        projectId,
        medicalReportTypeId,
        options: {enabled: false}
    })

    return ObjectKeys(sectionsMap).map(sectionKey => {
        const title = t(sectionsMap[sectionKey].title)
        const description = sectionsMap[sectionKey]?.description ? t(sectionsMap[sectionKey].description as TKey) : null
        const previousReport = previousProjectReportQuery?.data?.medicalRecordReportAttributes?.filter(
            attribute => attribute.name == sectionKey
        )?.[0]?.value

        return (
            <FormSection key={sectionKey} title={title} description={description ?? ''} hasBottomDivider={hasDividers}>
                <Controller
                    control={form.control}
                    name={sectionKey}
                    render={({field, fieldState}) => (
                        <MedicalReportTextArea
                            rows={4}
                            maxLength={sectionsMaxLength[sectionKey] ?? REPORT_TEXTAREA_LIMIT}
                            currentField={sectionKey}
                            title={title}
                            description={description ?? ''}
                            previousReport={previousReport}
                            placeholder={t('commons:description_dot')}
                            errorMessage={fieldState.error?.message}
                            onChange={field.onChange}
                            value={field.value ?? ''}
                        />
                    )}
                />
            </FormSection>
        )
    })
}
